export const WEEKDAY_LABELS_RU = ["Пн", "Вт", "Ср", "Чт", "Пт", "Сб", "Вс"];

export const MONTH_LABELS_RU = [
  "Январь",
  "Февраль",
  "Март",
  "Апрель",
  "Май",
  "Июнь",
  "Июль",
  "Август",
  "Сентябрь",
  "Октябрь",
  "Ноябрь",
  "Декабрь",
];

function pad2(value: number): string {
  return String(value).padStart(2, "0");
}

export { pad2 };

export function isoDatePart(value: string): string {
  return value.trim().slice(0, 10);
}

export function isIsoDateInRange(
  iso: string,
  min?: string,
  max?: string,
): boolean {
  const day = isoDatePart(iso);
  if (min && day < isoDatePart(min)) return false;
  if (max && day > isoDatePart(max)) return false;
  return true;
}

export function formatIsoDate(date: Date): string {
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
}

export function parseIsoDate(value: string): Date | null {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
}

export function isoToDisplayDate(iso: string): string {
  const date = parseIsoDateTime(iso);
  if (!date) return "";
  return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${date.getFullYear()}`;
}

export function displayDateToIso(display: string): string | null {
  const match = /^(\d{2})\.(\d{2})\.(\d{4})$/.exec(display.trim());
  if (!match) return null;
  const day = Number(match[1]);
  const month = Number(match[2]);
  const year = Number(match[3]);
  const date = new Date(year, month - 1, day);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return formatIsoDate(date);
}

export function maskDisplayDate(raw: string): string {
  let value = raw.replace(/[^\d]/g, "");

  if ((Number(value.slice(0, 1)) || 0) > 3) value = value.slice(0, -1);
  if ((Number(value.slice(0, 2)) || 0) > 31) value = value.slice(0, -1);
  if ((Number(value.slice(2, 3)) || 0) > 1) value = value.slice(0, -1);
  if ((Number(value.slice(2, 4)) || 0) > 12) value = value.slice(0, -1);

  if (value.length < 3) return value;
  if (value.length < 5) return `${value.slice(0, 2)}.${value.slice(2)}`;
  if (value.length < 9) {
    return `${value.slice(0, 2)}.${value.slice(2, 4)}.${value.slice(4)}`;
  }
  return `${value.slice(0, 2)}.${value.slice(2, 4)}.${value.slice(4, 8)}`;
}

export function formatIsoDateTime(date: Date): string {
  return `${formatIsoDate(date)}T${pad2(date.getHours())}:${pad2(date.getMinutes())}:${pad2(date.getSeconds())}`;
}

export function parseIsoDateTime(value: string): Date | null {
  const trimmed = value.trim();
  const dateOnly = parseIsoDate(trimmed);
  if (dateOnly) return dateOnly;

  const match = /^(\d{4})-(\d{2})-(\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(
    trimmed,
  );
  if (!match) return null;
  const year = Number(match[1]);
  const month = Number(match[2]);
  const day = Number(match[3]);
  const hours = Number(match[4]);
  const minutes = Number(match[5]);
  const seconds = Number(match[6] ?? "0");
  const date = new Date(year, month - 1, day, hours, minutes, seconds);
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day ||
    date.getHours() !== hours ||
    date.getMinutes() !== minutes
  ) {
    return null;
  }
  return date;
}

export function isoToDisplayTime(iso: string): string {
  const date = parseIsoDateTime(iso);
  if (!date) return "";
  return `${pad2(date.getHours())}:${pad2(date.getMinutes())}`;
}

export function maskDisplayTime(raw: string): string {
  let value = raw.replace(/[^\d]/g, "");

  if (value.length >= 1 && Number(value[0]) > 2) value = value.slice(0, -1);
  if (value.length >= 2 && Number(value.slice(0, 2)) > 23) {
    value = `23${value.slice(2)}`;
  }
  if (value.length >= 3 && Number(value[2]) > 5) value = value.slice(0, 2);
  if (value.length > 4) value = value.slice(0, 4);

  if (value.length < 3) return value;
  return `${value.slice(0, 2)}:${value.slice(2)}`;
}

export function displayTimeToHm(
  display: string,
): { hours: number; minutes: number } | null {
  const match = /^(\d{2}):(\d{2})$/.exec(display.trim());
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return { hours, minutes };
}

export function combineDisplayDateTime(
  dateDisplay: string,
  timeDisplay: string,
  fallbackTime: { hours: number; minutes: number },
): string | null {
  const isoDate = displayDateToIso(dateDisplay);
  if (!isoDate) return null;
  const time = displayTimeToHm(timeDisplay) ?? fallbackTime;
  const date = parseIsoDate(isoDate);
  if (!date) return null;
  date.setHours(time.hours, time.minutes, 0, 0);
  return formatIsoDateTime(date);
}

export type CalendarDay = {
  iso: string;
  day: number;
  inMonth: boolean;
};

export function buildCalendarDays(year: number, month0: number): CalendarDay[] {
  const first = new Date(year, month0, 1);
  const weekday = (first.getDay() + 6) % 7;
  const start = new Date(year, month0, 1 - weekday);
  const days: CalendarDay[] = [];

  for (let index = 0; index < 42; index += 1) {
    const date = new Date(
      start.getFullYear(),
      start.getMonth(),
      start.getDate() + index,
    );
    days.push({
      iso: formatIsoDate(date),
      day: date.getDate(),
      inMonth: date.getMonth() === month0,
    });
  }

  return days;
}
